/**
 * AdherenceCard — shows daily adherence metrics vs the active plan.
 *
 * Security (CLAUDE.md §4): all strings via React text nodes.
 *
 * Accessibility (CLAUDE.md §7, spec tracking-dashboard):
 *   - section > h2 with aria-labelledby
 *   - aria-live="polite" on the dynamic data region
 *   - met/missed signaled via text label AND color (never color alone)
 *   - NO_ACTIVE_PLAN state: explicit user-facing message
 *   - Controls >= 44×44 px (no interactive controls in this component)
 *   - Loading: role="status"
 *   - Error: role="alert"
 *
 * States: loading / error / NO_ACTIVE_PLAN / ok.
 */

import type { Adherence } from "@autodieta/shared";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type AdherenceResult =
  | { status: "ok"; adherence: Adherence }
  | { status: "NO_ACTIVE_PLAN" };

export interface AdherenceCardProps {
  result?: AdherenceResult;
  loading?: boolean;
  error?: string;
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

const HEADING_ID = "adherence-heading";

export function AdherenceCard({ result, loading, error }: AdherenceCardProps): JSX.Element {
  if (loading) {
    return (
      <section aria-labelledby={HEADING_ID}>
        <h2 id={HEADING_ID} className="mb-3 text-base font-semibold text-zinc-100">
          Adherencia del día
        </h2>
        <div
          role="status"
          aria-label="Cargando adherencia"
          className="flex flex-col gap-3 rounded-xl border border-zinc-700 bg-zinc-900 p-4"
        >
          <div className="h-4 w-48 animate-pulse rounded bg-zinc-700" />
          <div className="h-4 w-full animate-pulse rounded bg-zinc-800" />
          <span className="sr-only">Cargando datos de adherencia…</span>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section aria-labelledby={HEADING_ID}>
        <h2 id={HEADING_ID} className="mb-3 text-base font-semibold text-zinc-100">
          Adherencia del día
        </h2>
        <div
          role="alert"
          aria-live="assertive"
          className="rounded-xl border border-red-800/60 bg-red-950/20 px-4 py-3 text-sm text-red-400"
        >
          {error}
        </div>
      </section>
    );
  }

  if (!result) {
    return (
      <section aria-labelledby={HEADING_ID}>
        <h2 id={HEADING_ID} className="mb-3 text-base font-semibold text-zinc-100">
          Adherencia del día
        </h2>
        <p className="py-4 text-center text-sm text-zinc-500">
          Registrá tu primera comida para ver tu adherencia.
        </p>
      </section>
    );
  }

  if (result.status === "NO_ACTIVE_PLAN") {
    return (
      <section aria-labelledby={HEADING_ID}>
        <h2 id={HEADING_ID} className="mb-3 text-base font-semibold text-zinc-100">
          Adherencia del día
        </h2>
        <p className="py-4 text-center text-sm text-zinc-500">
          No tenés un plan activo. Solicitá un plan nutricional para ver tu adherencia.
        </p>
      </section>
    );
  }

  const { adherence } = result;

  return (
    <section aria-labelledby={HEADING_ID}>
      <h2 id={HEADING_ID} className="mb-3 text-base font-semibold text-zinc-100">
        Adherencia del día
      </h2>
      <div
        aria-live="polite"
        aria-label="Métricas de adherencia"
        className="rounded-xl border border-zinc-700 bg-zinc-900 p-4"
      >
        <dl className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          <MacroAdherence
            label="Energía"
            pct={adherence.pct.kcal}
            met={adherence.met.kcal}
          />
          <MacroAdherence
            label="Proteínas"
            pct={adherence.pct.proteinG}
            met={adherence.met.proteinG}
          />
          <MacroAdherence
            label="Carbohidratos"
            pct={adherence.pct.carbG}
            met={adherence.met.carbG}
          />
          <MacroAdherence
            label="Grasas"
            pct={adherence.pct.fatG}
            met={adherence.met.fatG}
          />
        </dl>
      </div>
    </section>
  );
}

// ---------------------------------------------------------------------------
// MacroAdherence — single macro metric
// ---------------------------------------------------------------------------

interface MacroAdherenceProps {
  label: string;
  pct: number;
  met: boolean;
}

function MacroAdherence({ label, pct, met }: MacroAdherenceProps): JSX.Element {
  const pctDisplay = `${Math.round(pct * 100)} %`;

  return (
    <div className="flex flex-col gap-1">
      <dt className="text-xs text-zinc-500">{label}</dt>
      <dd className="font-semibold text-zinc-100">{pctDisplay}</dd>
      {/* met/missed: text label + color — NEVER color alone (WCAG) */}
      <MetBadge met={met} />
    </div>
  );
}

function MetBadge({ met }: { met: boolean }): JSX.Element {
  if (met) {
    return (
      <span
        className="inline-flex items-center gap-1 text-xs font-medium text-emerald-400"
        aria-label="Cumplido"
      >
        {/* Text label + icon — never color alone */}
        <span aria-hidden="true">✓</span>
        <span>Cumplido</span>
      </span>
    );
  }

  return (
    <span
      className="inline-flex items-center gap-1 text-xs font-medium text-amber-400"
      aria-label="No cumplido"
    >
      <span aria-hidden="true">✗</span>
      <span>No cumplido</span>
    </span>
  );
}
